import React, { useEffect, useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, ArrowUp, ArrowRight, Check, ChevronRight, X, Package, Sparkles, Stamp, Bell, Video, ScrollText, CalendarClock, Tv, UserCircle, Zap, GitCompare, Tag, Ticket, DoorOpen, Crown as CrownIcon, Award, Coins, Sparkle, Gem, Camera, HandHeart, Handshake, Compass, Vote, Trophy, Frame, Map as MapIcon, Palette, Star as StarIcon, User as UserIcon, ListOrdered, Layers, Utensils, ArrowUpCircle, Mail, Calendar, Truck, Sparkles as SparkleIcon, Clock, PartyPopper, Loader2 } from 'lucide-react';
const CornerOrnaments = () => <>
    <span className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l border-[#D4AF37]/50 pointer-events-none" data-id="element-2015" />
    <span className="absolute top-0 right-0 w-2.5 h-2.5 border-t border-r border-[#D4AF37]/50 pointer-events-none" data-id="element-2016" />
    <span className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l border-[#D4AF37]/50 pointer-events-none" data-id="element-2017" />
    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r border-[#D4AF37]/50 pointer-events-none" data-id="element-2018" />
  </>;
type Category = 'All' | 'Online' | 'Event' | 'Prestige';
type Fulfillment = 'instant' | 'access_grant' | 'schedule' | 'shipping' | 'reservation' | 'form';
// ---------- Duplicates ----------
const duplicates = [{
  id: 'd1',
  name: 'Ranjit Singh',
  rarity: 'Treasury Rare',
  xp: 250,
  img: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
  rarityColor: 'text-[#D4AF37]/90 border-[#D4AF37]/50'
}, {
  id: 'd2',
  name: 'Harmandir Sahib',
  rarity: 'Epic',
  xp: 750,
  img: 'https://cdn.magicpatterns.com/uploads/st6W3BtxYpFsMW4u23CAxv/Harmandir_Sahib_Amritsar.png',
  rarityColor: 'text-[#8B0000] border-[#8B0000]/50'
}, {
  id: 'd3',
  name: 'Hari Singh Nalwa',
  rarity: 'Common',
  xp: 50,
  img: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png',
  rarityColor: 'text-[#FDFBF7]/70 border-[#FDFBF7]/20'
}, {
  id: 'd4',
  name: 'Moran Sarkar',
  rarity: 'Treasury Rare',
  xp: 250,
  img: 'https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png',
  rarityColor: 'text-[#D4AF37]/90 border-[#D4AF37]/50'
}];
type Reward = {
  id: string;
  name: string;
  category: 'Online' | 'Event' | 'Prestige';
  cost: number;
  desc: string;
  icon: React.ElementType;
  fulfillment: Fulfillment;
  featured?: boolean;
  /** What appears in the modal as the post-redemption explainer */
  outcome?: string;
};
// ---------- The curated 36 ----------
const allRewards: Reward[] = [
// ---- ONLINE (12) ----
{
  id: 'o1',
  name: 'Bonus Court Packet',
  category: 'Online',
  cost: 500,
  icon: Package,
  fulfillment: 'instant',
  desc: 'A free bonus pack to open right now.',
  outcome: 'Your bonus packet is in your collection — open it from your inventory.'
}, {
  id: 'o2',
  name: 'Rare Card Unlock',
  category: 'Online',
  cost: 1500,
  icon: Sparkles,
  fulfillment: 'instant',
  desc: 'One guaranteed Rare-tier card.',
  outcome: 'Your Rare card has been added to your collection.'
}, {
  id: 'o3',
  name: 'Exclusive Digital Seal',
  category: 'Online',
  cost: 400,
  icon: Stamp,
  fulfillment: 'instant',
  desc: 'A seal that appears on your profile and cards.',
  outcome: 'Your new seal is now visible on your profile and all future cards.'
}, {
  id: 'o4',
  name: 'Early Access · Collection Drop',
  category: 'Online',
  cost: 2000,
  icon: Bell,
  fulfillment: 'access_grant',
  desc: '24-hour head start on the next collection.',
  outcome: 'You\u2019ll receive private access 24 hours before the public drop.'
}, {
  id: 'o5',
  name: 'Behind-the-Scenes Video',
  category: 'Online',
  cost: 800,
  icon: Video,
  fulfillment: 'access_grant',
  desc: 'Private studio video from the making.',
  outcome: 'Your private video is now available to stream from your account.'
}, {
  id: 'o6',
  name: 'Historian Commentary Clip',
  category: 'Online',
  cost: 1100,
  icon: ScrollText,
  fulfillment: 'access_grant',
  desc: 'A historian breaks down one moment in depth.',
  outcome: 'Your historian clip is now in your private archive.'
}, {
  id: 'o7',
  name: 'Priority · Treasury Night Registration',
  category: 'Online',
  cost: 2500,
  icon: CalendarClock,
  fulfillment: 'reservation',
  desc: 'Skip the queue — register hours before the public window.',
  outcome: 'Priority window is locked. You\u2019ll get the registration link 6 hours early.'
}, {
  id: 'o8',
  name: 'Virtual Creators Session',
  category: 'Online',
  cost: 3000,
  icon: Tv,
  fulfillment: 'schedule',
  desc: 'Live Zoom with the creator team. Q&amp;A welcome.',
  outcome: 'Your session is booked. Calendar invite & Zoom link sent to your inbox.'
}, {
  id: 'o9',
  name: 'Featured Collector Profile',
  category: 'Online',
  cost: 3500,
  icon: UserCircle,
  fulfillment: 'form',
  desc: 'Featured on the Lahore Treasury collector wall.',
  outcome: 'Your submission is in. Our team will reach out for photos & copy within 5 days.'
}, {
  id: 'o10',
  name: 'XP Multiplier · 7 Days',
  category: 'Online',
  cost: 1800,
  icon: Zap,
  fulfillment: 'instant',
  desc: '1.5\u00d7 XP on every action for one week.',
  outcome: 'Your 1.5\u00d7 boost is active now and runs for the next 7 days.'
}, {
  id: 'o11',
  name: 'Source-to-Reconstruction Compare',
  category: 'Online',
  cost: 1600,
  icon: GitCompare,
  fulfillment: 'access_grant',
  desc: 'See historical source side-by-side with the build.',
  outcome: 'Comparison archive unlocked in your account.'
}, {
  id: 'o12',
  name: 'Court Designation · Digital Title',
  category: 'Online',
  cost: 4500,
  icon: Tag,
  fulfillment: 'form',
  desc: 'A custom Court rank displayed beneath your name.',
  outcome: 'Your title request is in review. Court approval & assignment in 2\u20133 days.'
},
// ---- EVENT (14) ----
{
  id: 'e1',
  name: 'Preview Ticket',
  category: 'Event',
  cost: 8000,
  icon: Ticket,
  fulfillment: 'schedule',
  desc: 'Standard preview ticket to the immersive show.',
  outcome: 'Your preview ticket is reserved. Confirmation email on its way.'
}, {
  id: 'e2',
  name: 'Priority Preview Night Admission',
  category: 'Event',
  cost: 12000,
  icon: DoorOpen,
  fulfillment: 'schedule',
  desc: 'Guaranteed entry on opening preview night.',
  outcome: 'Opening night admission secured. Date locked.'
}, {
  id: 'e3',
  name: 'VIP Check-In · Priority Line',
  category: 'Event',
  cost: 4500,
  icon: CrownIcon,
  fulfillment: 'reservation',
  desc: 'Skip the line with VIP host check-in.',
  outcome: 'VIP check-in is on your ticket. Show this code at the door.'
}, {
  id: 'e4',
  name: 'Treasury Night · Access',
  category: 'Event',
  cost: 10000,
  icon: Sparkle,
  fulfillment: 'schedule',
  desc: 'Entry to a numbered Treasury Night.',
  outcome: 'You\u2019re on the Treasury Night list for your chosen evening.'
}, {
  id: 'e5',
  name: 'Court Session · Live Access',
  category: 'Event',
  cost: 14000,
  icon: Layers,
  fulfillment: 'schedule',
  desc: 'Live Court Session — small-format & intimate.',
  outcome: 'A reserved seat is held in your name.'
}, {
  id: 'e6',
  name: 'Event-Only Collectible Card',
  category: 'Event',
  cost: 5500,
  icon: Layers,
  fulfillment: 'shipping',
  desc: 'A printed card never released online — event drop only.',
  outcome: 'Your card is being prepared. Ships in 3\u20134 weeks.'
}, {
  id: 'e7',
  name: 'Event-Only Commemorative Print',
  category: 'Event',
  cost: 6500,
  icon: Frame,
  fulfillment: 'shipping',
  desc: 'A printed run only available at this event.',
  outcome: 'Your commemorative print is being prepared. Ships in 3\u20134 weeks.'
}, {
  id: 'e8',
  name: 'Ranjit Singh Commemorative Coin',
  category: 'Event',
  cost: 15000,
  icon: Coins,
  fulfillment: 'shipping',
  desc: 'Limited-mint brass coin — the founding piece.',
  outcome: 'Your numbered coin is being struck and prepared. Ships in 4\u20136 weeks.',
  featured: true
}, {
  id: 'e9',
  name: 'Turban Tying Experience',
  category: 'Event',
  cost: 9000,
  icon: HandHeart,
  fulfillment: 'schedule',
  desc: 'Live turban tying by a master practitioner.',
  outcome: 'Your slot is reserved. Bring photo ID — host will meet you at check-in.'
}, {
  id: 'e10',
  name: 'Jewelry Experience for Women',
  category: 'Event',
  cost: 11000,
  icon: Gem,
  fulfillment: 'schedule',
  desc: 'A guided heritage jewelry styling experience.',
  outcome: 'Your appointment is reserved. Stylist will reach out 48 hours prior.'
}, {
  id: 'e11',
  name: 'Costume &amp; Styling Photo Moment',
  category: 'Event',
  cost: 10000,
  icon: Camera,
  fulfillment: 'schedule',
  desc: 'Dress in court styling — professional photos included.',
  outcome: 'Your styling appointment is reserved. Photos delivered in 2 weeks.'
}, {
  id: 'e12',
  name: 'Meet the Creators',
  category: 'Event',
  cost: 15000,
  icon: Handshake,
  fulfillment: 'schedule',
  desc: 'A private meet with the team behind the work.',
  outcome: 'Your private meet is scheduled. The team will be expecting you.'
}, {
  id: 'e13',
  name: 'Signed Collectible &amp; Certificate',
  category: 'Event',
  cost: 16000,
  icon: Award,
  fulfillment: 'shipping',
  desc: 'A signed collectible with numbered certificate.',
  outcome: 'Your signed piece is being prepared. Ships in 4\u20136 weeks.'
}, {
  id: 'e14',
  name: 'Standard \u2192 Private Preview Upgrade',
  category: 'Event',
  cost: 18000,
  icon: ArrowUpCircle,
  fulfillment: 'reservation',
  desc: 'Upgrade your ticket to an intimate private preview.',
  outcome: 'Your ticket has been upgraded. Private preview details emailed.'
},
// ---- PRESTIGE (10) ----
{
  id: 'p1',
  name: 'Featured as a Bystander in the Experience',
  category: 'Prestige',
  cost: 35000,
  icon: UserIcon,
  fulfillment: 'form',
  desc: 'Your likeness rendered into a scene — forever.',
  outcome: 'Your request is in. We\u2019ll arrange a photo session and confirm scene placement.',
  featured: true
}, {
  id: 'p2',
  name: 'Patron Wall · In-Experience Name',
  category: 'Prestige',
  cost: 22000,
  icon: ListOrdered,
  fulfillment: 'form',
  desc: 'Your name etched onto the in-experience patron wall.',
  outcome: 'Your patron name is locked. Wall update happens at the next opening.'
}, {
  id: 'p3',
  name: 'Founder Coin · Limited Mint',
  category: 'Prestige',
  cost: 30000,
  icon: Coins,
  fulfillment: 'shipping',
  desc: 'A numbered founder coin — first 100 only.',
  outcome: 'Your founder coin is being struck. Ships in 6\u20138 weeks with certificate.',
  featured: true
}, {
  id: 'p4',
  name: 'Invite-Only Creators Dinner',
  category: 'Prestige',
  cost: 40000,
  icon: Utensils,
  fulfillment: 'schedule',
  desc: 'A closed-door dinner with the creator team.',
  outcome: 'Your seat at the dinner is held. Date, venue, and dress code follow by email.'
}, {
  id: 'p5',
  name: 'Private Walkthrough with Creators',
  category: 'Prestige',
  cost: 32000,
  icon: Compass,
  fulfillment: 'schedule',
  desc: 'Walk the experience after-hours with the creators.',
  outcome: 'Your private walkthrough is scheduled.'
}, {
  id: 'p6',
  name: 'Vote on a Future Drop Theme',
  category: 'Prestige',
  cost: 16000,
  icon: Vote,
  fulfillment: 'instant',
  desc: 'Cast a binding vote on the next collectible theme.',
  outcome: 'Your vote is recorded. Results announced in the next Court Council.'
}, {
  id: 'p7',
  name: 'Treasury Title · One-Time',
  category: 'Prestige',
  cost: 50000,
  icon: Trophy,
  fulfillment: 'form',
  desc: 'A title minted only once — never re-issued.',
  outcome: 'Your title request is sealed for Court review. Approval & ceremony to follow.',
  featured: true
}, {
  id: 'p8',
  name: 'Private Archival Print · Framed',
  category: 'Prestige',
  cost: 24000,
  icon: Frame,
  fulfillment: 'shipping',
  desc: 'Framed archival print, hand-signed.',
  outcome: 'Your framed archival print is being prepared. Ships in 6\u20138 weeks.'
}, {
  id: 'p9',
  name: 'Priority for Future City Previews',
  category: 'Prestige',
  cost: 30000,
  icon: MapIcon,
  fulfillment: 'reservation',
  desc: 'First-priority for every new city we open.',
  outcome: 'You\u2019re on the priority list for every future city preview, indefinitely.'
}, {
  id: 'p10',
  name: 'Custom Commissioned Collectible',
  category: 'Prestige',
  cost: 75000,
  icon: Palette,
  fulfillment: 'form',
  desc: 'A one-of-one collectible — built with you.',
  outcome: 'Your commission request is received. A creator lead will be in touch within 7 days.',
  featured: true
}];
const categoryAccent: Record<Category, string> = {
  All: '',
  Online: 'from-[#D4AF37]/10 via-transparent to-transparent',
  Event: 'from-[#8B0000]/15 via-transparent to-transparent',
  Prestige: 'from-[#D4AF37]/20 via-[#8B0000]/10 to-transparent'
};
// =====================================================================
// Redemption Modal — different flows per fulfillment type
// =====================================================================
type RedemptionStep = 'input' | 'processing' | 'success';
const RedemptionModal = ({
  reward,
  onClose,
  onConfirm
}: {
  reward: Reward;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  const [step, setStep] = useState<RedemptionStep>('input');
  const [dateChoice, setDateChoice] = useState<string>('');
  const [timeChoice, setTimeChoice] = useState<string>('');
  const [shipping, setShipping] = useState({
    name: '',
    street: '',
    city: '',
    postal: '',
    country: ''
  });
  const [formText, setFormText] = useState('');
  // Block body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
  const advance = () => {
    setStep('processing');
    setTimeout(() => {
      setStep('success');
      onConfirm();
    }, 900);
  };
  const Icon = reward.icon;
  // ---- Flow-specific titles & CTAs ----
  const flowMeta: Record<Fulfillment, {
    title: string;
    subtitle: string;
    cta: string;
  }> = {
    instant: {
      title: 'Confirm Redemption',
      subtitle: 'This reward activates immediately on your account.',
      cta: 'Activate Now'
    },
    access_grant: {
      title: 'Unlock This Reward',
      subtitle: 'Access will be added to your account on confirmation.',
      cta: 'Unlock Access'
    },
    schedule: {
      title: 'Schedule Your Slot',
      subtitle: 'Choose a date and time. We\u2019ll send a calendar invite.',
      cta: 'Confirm Booking'
    },
    shipping: {
      title: 'Where Should We Ship?',
      subtitle: 'This reward is a physical piece. Tell us where to send it.',
      cta: 'Confirm &amp; Ship'
    },
    reservation: {
      title: 'Reserve This Privilege',
      subtitle: 'We\u2019ll add this to your account and send confirmation.',
      cta: 'Reserve Now'
    },
    form: {
      title: 'A Few Quick Details',
      subtitle: 'This reward needs a short note before it can be fulfilled.',
      cta: 'Submit Request'
    }
  };
  const meta = flowMeta[reward.fulfillment];
  // ---- Validators ----
  const canConfirm = (() => {
    if (reward.fulfillment === 'schedule') return dateChoice && timeChoice;
    if (reward.fulfillment === 'shipping') return shipping.name && shipping.street && shipping.city && shipping.postal && shipping.country;
    if (reward.fulfillment === 'form') return formText.trim().length >= 6;
    return true;
  })();
  // ---- Mock schedule data ----
  const dateOptions = ['Sat · Aug 9', 'Sat · Aug 16', 'Sat · Aug 23', 'Sat · Aug 30'];
  const timeOptions = ['6:30 PM', '7:30 PM', '8:30 PM'];
  // ---- Form prompt per item ----
  const formPrompt: Record<string, string> = {
    o9: 'Tell us about your collection journey in a few sentences.',
    o12: 'What title would you like? (e.g., "Keeper of the Eastern Court")',
    p1: 'Tell us which scene you\u2019d like to appear in and any preferences.',
    p2: 'How would you like your name to appear on the patron wall?',
    p7: 'Propose your one-time Treasury title and why.',
    p10: 'Describe the collectible you\u2019d like commissioned.'
  };
  return <AnimatePresence data-id="element-2019">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-2020">
        {/* Backdrop */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} onClick={step === 'success' ? onClose : undefined} className="absolute inset-0 bg-black/80 backdrop-blur-sm" data-id="element-2021" />

        {/* Modal */}
        <motion.div initial={{
        y: 20,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 10,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-[#0B0F19] border border-[#D4AF37]/40 shadow-[0_0_80px_rgba(212,175,55,0.15)]" data-id="element-2022">
          <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-2023" />
          <CornerOrnaments data-id="element-2024" />

          {/* Close */}
          {step !== 'processing' && <button onClick={onClose} className="absolute top-4 right-4 z-10 w-8 h-8 border border-[#D4AF37]/30 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] transition-colors" data-id="element-2025">
              <X className="w-4 h-4" data-id="element-2026" />
            </button>}

          {/* Header — reward identity */}
          <div className="px-6 sm:px-8 pt-7 pb-5 border-b border-[#D4AF37]/15" data-id="element-2027">
            <div className="flex items-center gap-4 mb-4" data-id="element-2028">
              <div className="shrink-0 w-14 h-14 border border-[#D4AF37]/50 bg-[#131B2B] flex items-center justify-center" data-id="element-2029">
                <Icon className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.25} data-id="element-2030" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-2031">
                <p className="text-[#D4AF37]/70 text-[9px] tracking-[0.3em] uppercase mb-1" data-id="element-2032">
                  {reward.category}
                  {reward.featured && <span className="ml-2 text-[#8B0000]" data-id="element-2033">· Marquee</span>}
                </p>
                <h3 className="font-serif text-xl text-[#FDFBF7] leading-tight truncate" dangerouslySetInnerHTML={{
                __html: reward.name
              }} data-id="element-2034" />
              </div>
              <div className="text-right shrink-0" data-id="element-2035">
                <p className="font-serif text-2xl text-[#D4AF37] leading-tight" data-id="element-2036">
                  {reward.cost.toLocaleString()}
                </p>
                <p className="text-[#FDFBF7]/40 text-[9px] tracking-[0.25em] uppercase" data-id="element-2037">
                  XP
                </p>
              </div>
            </div>

            {/* Progress dots */}
            <div className="flex items-center gap-1.5" data-id="element-2038">
              {(['input', 'processing', 'success'] as RedemptionStep[]).map((s, i) => {
              const active = step === s;
              const past = (['input', 'processing', 'success'] as const).indexOf(step) > i;
              return <span key={s} className={`h-1 flex-1 transition-colors ${active ? 'bg-[#D4AF37]' : past ? 'bg-[#D4AF37]/60' : 'bg-[#D4AF37]/15'}`} data-id="element-2039" />;
            })}
            </div>
          </div>

          {/* Body */}
          <div className="px-6 sm:px-8 py-6" data-id="element-2040">
            {step === 'input' && <>
                <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-2041">
                  {meta.title}
                </p>
                <p className="text-[#FDFBF7]/65 text-sm leading-relaxed mb-6" dangerouslySetInnerHTML={{
              __html: meta.subtitle
            }} data-id="element-2042" />

                {/* INSTANT / ACCESS_GRANT / RESERVATION — confirmation only */}
                {(reward.fulfillment === 'instant' || reward.fulfillment === 'access_grant' || reward.fulfillment === 'reservation') && <div className="border border-[#D4AF37]/20 bg-[#131B2B] p-4 mb-6" data-id="element-2043">
                    <p className="text-[#FDFBF7]/80 text-sm leading-relaxed flex items-start gap-3" data-id="element-2044">
                      <SparkleIcon className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" data-id="element-2045" />
                      <span data-id="element-2046">
                        <span className="block font-serif text-[#FDFBF7] mb-1" data-id="element-2047">
                          What you&rsquo;re getting
                        </span>
                        <span className="text-[#FDFBF7]/60 text-xs" dangerouslySetInnerHTML={{
                    __html: reward.desc
                  }} data-id="element-2048" />
                      </span>
                    </p>
                  </div>}

                {/* SCHEDULE */}
                {reward.fulfillment === 'schedule' && <div className="space-y-5 mb-6" data-id="element-2049">
                    <div data-id="element-2050">
                      <p className="text-[#FDFBF7]/70 text-[10px] tracking-[0.3em] uppercase mb-2.5 flex items-center gap-2" data-id="element-2051">
                        <Calendar className="w-3 h-3 text-[#D4AF37]" data-id="element-2052" />
                        Pick a Date
                      </p>
                      <div className="grid grid-cols-2 gap-2" data-id="element-2053">
                        {dateOptions.map(d => {
                    const active = dateChoice === d;
                    return <button key={d} onClick={() => setDateChoice(d)} className={`px-3 py-2.5 text-xs tracking-wide border transition-colors text-left ${active ? 'bg-[#D4AF37] text-[#0B0F19] border-[#D4AF37] font-bold' : 'border-[#D4AF37]/25 text-[#FDFBF7]/70 hover:border-[#D4AF37]/60'}`} data-id="element-2054">
                              {d}
                            </button>;
                  })}
                      </div>
                    </div>
                    <div data-id="element-2055">
                      <p className="text-[#FDFBF7]/70 text-[10px] tracking-[0.3em] uppercase mb-2.5 flex items-center gap-2" data-id="element-2056">
                        <Clock className="w-3 h-3 text-[#D4AF37]" data-id="element-2057" />
                        Pick a Time
                      </p>
                      <div className="grid grid-cols-3 gap-2" data-id="element-2058">
                        {timeOptions.map(t => {
                    const active = timeChoice === t;
                    return <button key={t} onClick={() => setTimeChoice(t)} className={`px-3 py-2.5 text-xs tracking-wide border transition-colors ${active ? 'bg-[#D4AF37] text-[#0B0F19] border-[#D4AF37] font-bold' : 'border-[#D4AF37]/25 text-[#FDFBF7]/70 hover:border-[#D4AF37]/60'}`} data-id="element-2059">
                              {t}
                            </button>;
                  })}
                      </div>
                    </div>
                  </div>}

                {/* SHIPPING */}
                {reward.fulfillment === 'shipping' && <div className="space-y-3 mb-6" data-id="element-2060">
                    <input value={shipping.name} onChange={e => setShipping({
                ...shipping,
                name: e.target.value
              })} placeholder="Full name" className="w-full bg-[#131B2B] border border-[#D4AF37]/25 px-3.5 py-3 text-sm text-[#FDFBF7] placeholder:text-[#FDFBF7]/30 focus:outline-none focus:border-[#D4AF37]/60" data-id="element-2061" />
                    <input value={shipping.street} onChange={e => setShipping({
                ...shipping,
                street: e.target.value
              })} placeholder="Street address" className="w-full bg-[#131B2B] border border-[#D4AF37]/25 px-3.5 py-3 text-sm text-[#FDFBF7] placeholder:text-[#FDFBF7]/30 focus:outline-none focus:border-[#D4AF37]/60" data-id="element-2062" />
                    <div className="grid grid-cols-2 gap-3" data-id="element-2063">
                      <input value={shipping.city} onChange={e => setShipping({
                  ...shipping,
                  city: e.target.value
                })} placeholder="City" className="w-full bg-[#131B2B] border border-[#D4AF37]/25 px-3.5 py-3 text-sm text-[#FDFBF7] placeholder:text-[#FDFBF7]/30 focus:outline-none focus:border-[#D4AF37]/60" data-id="element-2064" />
                      <input value={shipping.postal} onChange={e => setShipping({
                  ...shipping,
                  postal: e.target.value
                })} placeholder="Postal / ZIP" className="w-full bg-[#131B2B] border border-[#D4AF37]/25 px-3.5 py-3 text-sm text-[#FDFBF7] placeholder:text-[#FDFBF7]/30 focus:outline-none focus:border-[#D4AF37]/60" data-id="element-2065" />
                    </div>
                    <input value={shipping.country} onChange={e => setShipping({
                ...shipping,
                country: e.target.value
              })} placeholder="Country" className="w-full bg-[#131B2B] border border-[#D4AF37]/25 px-3.5 py-3 text-sm text-[#FDFBF7] placeholder:text-[#FDFBF7]/30 focus:outline-none focus:border-[#D4AF37]/60" data-id="element-2066" />
                    <p className="text-[#FDFBF7]/40 text-[10px] flex items-center gap-2" data-id="element-2067">
                      <Truck className="w-3 h-3" data-id="element-2068" />
                      We ship worldwide. Tracking emailed on dispatch.
                    </p>
                  </div>}

                {/* FORM */}
                {reward.fulfillment === 'form' && <div className="space-y-3 mb-6" data-id="element-2069">
                    <p className="text-[#FDFBF7]/70 text-[11px] leading-relaxed" data-id="element-2070">
                      {formPrompt[reward.id] || 'Tell us a bit about what you\u2019d like.'}
                    </p>
                    <textarea value={formText} onChange={e => setFormText(e.target.value)} rows={4} placeholder="A few sentences will do..." className="w-full bg-[#131B2B] border border-[#D4AF37]/25 px-3.5 py-3 text-sm text-[#FDFBF7] placeholder:text-[#FDFBF7]/30 focus:outline-none focus:border-[#D4AF37]/60 resize-none" data-id="element-2071" />
                    <p className="text-[#FDFBF7]/40 text-[10px] flex items-center gap-2" data-id="element-2072">
                      <Mail className="w-3 h-3" data-id="element-2073" />A team member will reach out
                      from court@lahoretreasury.com
                    </p>
                  </div>}

                {/* Cost breakdown */}
                <div className="border-t border-[#D4AF37]/15 pt-4 flex items-center justify-between mb-5" data-id="element-2074">
                  <div data-id="element-2075">
                    <p className="text-[#FDFBF7]/45 text-[10px] tracking-[0.25em] uppercase" data-id="element-2076">
                      Cost
                    </p>
                    <p className="font-serif text-lg text-[#FDFBF7]" data-id="element-2077">
                      {reward.cost.toLocaleString()}{' '}
                      <span className="text-[#FDFBF7]/40 text-xs" data-id="element-2078">XP</span>
                    </p>
                  </div>
                  <button onClick={advance} disabled={!canConfirm} className={`group inline-flex items-center justify-center gap-2 px-5 py-3 text-[10px] tracking-[0.3em] uppercase font-bold transition-all ${canConfirm ? 'bg-[#D4AF37] text-[#0B0F19] hover:bg-[#E5C158] shadow-[0_0_30px_rgba(212,175,55,0.3)]' : 'bg-[#D4AF37]/15 text-[#0B0F19]/40 cursor-not-allowed'}`} dangerouslySetInnerHTML={{
                __html: meta.cta + ' &nbsp; \u2192'
              }} data-id="element-2079" />
                </div>
              </>}

            {step === 'processing' && <div className="py-10 flex flex-col items-center justify-center text-center" data-id="element-2080">
                <Loader2 className="w-7 h-7 text-[#D4AF37] animate-spin mb-4" data-id="element-2081" />
                <p className="text-[#FDFBF7]/70 text-sm" data-id="element-2082">
                  Processing your redemption...
                </p>
                <p className="text-[#FDFBF7]/40 text-xs mt-1" data-id="element-2083">Hold tight</p>
              </div>}

            {step === 'success' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="py-2 flex flex-col items-center text-center" data-id="element-2084">
                <motion.div initial={{
              scale: 0.6,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 20
            }} className="w-14 h-14 border-2 border-[#D4AF37] bg-[#D4AF37]/10 flex items-center justify-center mb-4 shadow-[0_0_40px_rgba(212,175,55,0.4)]" data-id="element-2085">
                  <Check className="w-7 h-7 text-[#D4AF37]" strokeWidth={3} data-id="element-2086" />
                </motion.div>
                <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase font-bold mb-2 flex items-center gap-1.5" data-id="element-2087">
                  <PartyPopper className="w-3 h-3" data-id="element-2088" />
                  Redeemed
                </p>
                <h4 className="font-serif text-xl text-[#FDFBF7] leading-tight mb-3 max-w-sm" dangerouslySetInnerHTML={{
              __html: reward.name
            }} data-id="element-2089" />
                <p className="text-[#FDFBF7]/60 text-sm leading-relaxed max-w-sm mb-6" dangerouslySetInnerHTML={{
              __html: reward.outcome || 'Your reward has been processed.'
            }} data-id="element-2090" />

                {/* Flow-specific extras */}
                {reward.fulfillment === 'schedule' && <div className="w-full border border-[#D4AF37]/20 bg-[#131B2B] p-3.5 mb-5 flex items-center gap-3 text-left" data-id="element-2091">
                    <Calendar className="w-4 h-4 text-[#D4AF37] shrink-0" data-id="element-2092" />
                    <div className="flex-1 min-w-0" data-id="element-2093">
                      <p className="font-mono text-[#D4AF37] text-xs" data-id="element-2094">
                        {dateChoice} · {timeChoice}
                      </p>
                      <p className="text-[#FDFBF7]/40 text-[10px]" data-id="element-2095">
                        Calendar invite sent to your inbox
                      </p>
                    </div>
                  </div>}
                {reward.fulfillment === 'shipping' && <div className="w-full border border-[#D4AF37]/20 bg-[#131B2B] p-3.5 mb-5 flex items-center gap-3 text-left" data-id="element-2096">
                    <Truck className="w-4 h-4 text-[#D4AF37] shrink-0" data-id="element-2097" />
                    <div className="flex-1 min-w-0" data-id="element-2098">
                      <p className="font-mono text-[#D4AF37] text-xs truncate" data-id="element-2099">
                        {shipping.street}, {shipping.city}
                      </p>
                      <p className="text-[#FDFBF7]/40 text-[10px]" data-id="element-2100">
                        Tracking emailed on dispatch
                      </p>
                    </div>
                  </div>}
                {reward.fulfillment === 'access_grant' && <button className="w-full mb-5 inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#D4AF37]/50 text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase font-bold hover:bg-[#D4AF37]/10 transition-colors" data-id="element-2101">
                    Open in My Account
                    <ArrowRight className="w-3 h-3" data-id="element-2102" />
                  </button>}

                <button onClick={onClose} className="text-[#FDFBF7]/60 hover:text-[#FDFBF7] text-[10px] tracking-[0.3em] uppercase font-medium border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 px-5 py-2.5 transition-colors" data-id="element-2103">
                  Done
                </button>
              </motion.div>}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
};
// =====================================================================
// Main component
// =====================================================================
export const RewardsTabContent = () => {
  const [filter, setFilter] = useState<Category>('All');
  const [userXp, setUserXp] = useState(14820);
  const [returnedIds, setReturnedIds] = useState<string[]>([]);
  const [redeemedIds, setRedeemedIds] = useState<string[]>([]);
  const [redeeming, setRedeeming] = useState<Reward | null>(null);
  const nextTierXp = 25000;
  const tierProgress = Math.min(100, Math.round(userXp / nextTierXp * 100));
  const availableDuplicates = duplicates.filter(d => !returnedIds.includes(d.id));
  const totalDupXp = availableDuplicates.reduce((s, d) => s + d.xp, 0);
  const counts = {
    All: allRewards.length,
    Online: allRewards.filter(r => r.category === 'Online').length,
    Event: allRewards.filter(r => r.category === 'Event').length,
    Prestige: allRewards.filter(r => r.category === 'Prestige').length
  };
  const filtered = filter === 'All' ? allRewards : allRewards.filter(r => r.category === filter);
  const handleReturn = (id: string, xp: number) => {
    setReturnedIds(prev => [...prev, id]);
    setUserXp(prev => prev + xp);
  };
  const handleReturnAll = () => {
    setReturnedIds(prev => [...prev, ...availableDuplicates.map(d => d.id)]);
    setUserXp(prev => prev + totalDupXp);
  };
  const handleConfirmRedeem = () => {
    if (!redeeming) return;
    if (userXp < redeeming.cost) return;
    setRedeemedIds(prev => [...prev, redeeming.id]);
    setUserXp(prev => prev - redeeming.cost);
  };
  const fulfillmentBadge: Record<Fulfillment, {
    label: string;
    icon: React.ElementType;
  }> = {
    instant: {
      label: 'Instant',
      icon: Zap
    },
    access_grant: {
      label: 'Access',
      icon: DoorOpen
    },
    schedule: {
      label: 'Schedule',
      icon: Calendar
    },
    shipping: {
      label: 'Ships to you',
      icon: Truck
    },
    reservation: {
      label: 'Reserved',
      icon: Bell
    },
    form: {
      label: 'Bespoke',
      icon: Mail
    }
  };
  const categories: Category[] = ['All', 'Online', 'Event', 'Prestige'];
  return <>
      <div className="flex flex-col gap-8" data-id="element-2104">
        {/* Balance Hero */}
        <div className="relative border border-[#D4AF37]/30 bg-gradient-to-br from-[#0B0F19] via-[#131B2B] to-[#0B0F19] p-6 sm:p-7 overflow-hidden" data-id="element-2105">
          <CornerOrnaments data-id="element-2106" />
          <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-2107" />
          <div className="absolute -right-20 -top-20 w-64 h-64 bg-[#D4AF37]/10 blur-[100px] rounded-full pointer-events-none" data-id="element-2108" />
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center gap-6" data-id="element-2109">
            <div className="flex-1" data-id="element-2110">
              <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-2" data-id="element-2111">
                Your Balance
              </p>
              <div className="flex items-baseline gap-3 mb-4" data-id="element-2112">
                <motion.span key={userXp} initial={{
                scale: 1.1,
                color: '#FDFBF7'
              }} animate={{
                scale: 1,
                color: '#D4AF37'
              }} transition={{
                duration: 0.4
              }} className="font-serif text-5xl sm:text-6xl leading-none" data-id="element-2113">
                  {userXp.toLocaleString()}
                </motion.span>
                <span className="text-[#FDFBF7]/50 text-xs tracking-[0.25em] uppercase" data-id="element-2114">
                  XP
                </span>
              </div>
              <div className="flex items-center gap-3 mb-2" data-id="element-2115">
                <span className="text-[9px] tracking-[0.3em] uppercase text-[#D4AF37] border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-2 py-1" data-id="element-2116">
                  Darbar Tier
                </span>
                <ChevronRight className="w-3 h-3 text-[#D4AF37]/40" data-id="element-2117" />
                <span className="text-[9px] tracking-[0.3em] uppercase text-[#8B0000]/80" data-id="element-2118">
                  Prestige
                </span>
              </div>
              <div className="h-1 bg-[#D4AF37]/10 relative overflow-hidden max-w-md" data-id="element-2119">
                <motion.div initial={{
                width: 0
              }} animate={{
                width: `${tierProgress}%`
              }} transition={{
                duration: 0.8
              }} className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#D4AF37]/60 to-[#D4AF37]" data-id="element-2120" />
              </div>
              <p className="text-[#FDFBF7]/40 text-[10px] mt-2" data-id="element-2121">
                {Math.max(0, nextTierXp - userXp).toLocaleString()} XP to
                Prestige
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 lg:gap-4" data-id="element-2122">
              {[{
              label: 'Redeemed',
              value: redeemedIds.length
            }, {
              label: 'Returned',
              value: returnedIds.length
            }, {
              label: 'This Month',
              value: '+2,140'
            }].map(s => <div key={s.label} className="text-center border border-[#D4AF37]/15 bg-[#0B0F19]/60 px-4 py-3 min-w-[80px]" data-id="element-2123">
                  <p className="font-serif text-xl text-[#D4AF37] leading-tight" data-id="element-2124">
                    {s.value}
                  </p>
                  <p className="text-[#FDFBF7]/45 text-[9px] tracking-[0.25em] uppercase mt-1" data-id="element-2125">
                    {s.label}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* Return Cards for XP */}
        {availableDuplicates.length > 0 && <div className="border border-[#D4AF37]/20 bg-[#0B0F19] p-5 sm:p-6 relative" data-id="element-2126">
            <div className="flex items-start justify-between mb-5 gap-4" data-id="element-2127">
              <div data-id="element-2128">
                <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-1.5" data-id="element-2129">
                  Return Cards for XP
                </p>
                <h4 className="font-serif text-lg text-[#FDFBF7] leading-tight" data-id="element-2130">
                  Trade duplicates for XP
                </h4>
              </div>
              <div className="text-right shrink-0" data-id="element-2131">
                <p className="font-mono text-[#D4AF37] text-lg leading-tight" data-id="element-2132">
                  +{totalDupXp.toLocaleString()} XP
                </p>
                <p className="text-[#FDFBF7]/40 text-[9px] tracking-[0.25em] uppercase mt-0.5" data-id="element-2133">
                  If returned
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-2134">
              {availableDuplicates.map(d => <motion.div key={d.id} layout exit={{
            opacity: 0,
            scale: 0.9
          }} className="group relative border border-[#D4AF37]/15 bg-[#131B2B] hover:border-[#D4AF37]/40 transition-colors" data-id="element-2135">
                  <div className="aspect-[4/5] overflow-hidden relative" data-id="element-2136">
                    <img src={d.img} alt={d.name} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" data-id="element-2137" />
                    <span className={`absolute top-2 left-2 text-[8px] tracking-[0.2em] uppercase font-medium px-1.5 py-0.5 border bg-[#0B0F19]/80 ${d.rarityColor}`} data-id="element-2138">
                      {d.rarity}
                    </span>
                    <span className="absolute top-2 right-2 text-[9px] font-mono text-[#FDFBF7]/80 bg-[#0B0F19]/80 px-1.5 py-0.5 border border-[#D4AF37]/30" data-id="element-2139">
                      ×2
                    </span>
                  </div>
                  <div className="p-2.5" data-id="element-2140">
                    <p className="font-serif text-xs text-[#FDFBF7] truncate leading-tight" data-id="element-2141">
                      {d.name}
                    </p>
                    <button onClick={() => handleReturn(d.id, d.xp)} className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 text-[9px] tracking-[0.2em] uppercase font-bold border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors" data-id="element-2142">
                      <ArrowUp className="w-2.5 h-2.5" data-id="element-2143" />
                      Return +{d.xp}
                    </button>
                  </div>
                </motion.div>)}
            </div>
            <button onClick={handleReturnAll} className="w-full mt-4 py-2.5 text-[10px] tracking-[0.25em] uppercase font-bold bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-colors flex items-center justify-center gap-2" data-id="element-2144">
              Return All · +{totalDupXp.toLocaleString()} XP
              <ArrowRight className="w-3 h-3" data-id="element-2145" />
            </button>
          </div>}

        {/* Category Filter */}
        <div className="flex items-center justify-between gap-4 flex-wrap border-b border-[#D4AF37]/15 pb-4" data-id="element-2146">
          <div data-id="element-2147">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-1" data-id="element-2148">
              The Catalog
            </p>
            <p className="text-[#FDFBF7]/50 text-xs" data-id="element-2149">
              {filtered.length}{' '}
              {filter === 'All' ? 'rewards' : `${filter.toLowerCase()} rewards`}{' '}
              available
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-id="element-2150">
            {categories.map(c => {
            const active = filter === c;
            return <button key={c} onClick={() => setFilter(c)} className={`px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase transition-colors border flex items-center gap-2 ${active ? 'bg-[#D4AF37] text-[#0B0F19] border-[#D4AF37] font-bold' : 'border-[#D4AF37]/25 text-[#FDFBF7]/60 hover:text-[#FDFBF7] hover:border-[#D4AF37]/50'}`} data-id="element-2151">
                  {c}
                  <span className={`font-mono text-[9px] ${active ? 'text-[#0B0F19]/60' : 'text-[#FDFBF7]/35'}`} data-id="element-2152">
                    {counts[c]}
                  </span>
                </button>;
          })}
          </div>
        </div>

        {/* Catalog */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2153">
          {filtered.map(r => {
          const isRedeemed = redeemedIds.includes(r.id);
          const affordable = userXp >= r.cost && !isRedeemed;
          const shortfall = r.cost - userXp;
          const Icon = r.icon;
          const fb = fulfillmentBadge[r.fulfillment];
          return <motion.div key={r.id} layout className={`group relative border bg-[#0B0F19] flex flex-col transition-all ${isRedeemed ? 'border-[#D4AF37]/30 opacity-70' : affordable ? 'border-[#D4AF37]/40 hover:border-[#D4AF37] hover:shadow-[0_0_30px_rgba(212,175,55,0.15)]' : 'border-[#FDFBF7]/10 opacity-90 hover:opacity-100'}`} data-id="element-2154">
                {/* Icon header */}
                <div className={`relative aspect-[5/3] overflow-hidden border-b border-[#D4AF37]/10 ${affordable ? 'bg-[#131B2B]' : 'bg-[#0B0F19]'}`} data-id="element-2155">
                  <div className={`absolute inset-0 bg-gradient-to-br ${categoryAccent[r.category]} pointer-events-none`} data-id="element-2156" />
                  {affordable && <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.08),_transparent_70%)] pointer-events-none" data-id="element-2157" />}
                  <CornerOrnaments data-id="element-2158" />

                  <span className="absolute top-3 left-3 text-[9px] tracking-[0.25em] uppercase font-medium px-2 py-1 border border-[#D4AF37]/40 bg-[#0B0F19]/80 text-[#D4AF37] z-10" data-id="element-2159">
                    {r.category}
                  </span>
                  {r.featured && <span className="absolute top-3 left-[5.5rem] text-[8px] tracking-[0.25em] uppercase font-bold px-1.5 py-1 border border-[#8B0000]/60 bg-[#8B0000]/20 text-[#FDFBF7] z-10 flex items-center gap-1" data-id="element-2160">
                      <StarIcon className="w-2.5 h-2.5" data-id="element-2161" />
                      Marquee
                    </span>}
                  {!affordable && !isRedeemed && <div className="absolute top-3 right-3 w-7 h-7 border border-[#FDFBF7]/30 bg-[#0B0F19]/80 flex items-center justify-center z-10" data-id="element-2162">
                      <Lock className="w-3 h-3 text-[#FDFBF7]/50" data-id="element-2163" />
                    </div>}
                  {isRedeemed && <div className="absolute top-3 right-3 px-2 py-1 border border-[#D4AF37] bg-[#D4AF37] text-[#0B0F19] text-[9px] tracking-[0.2em] uppercase font-bold flex items-center gap-1 z-10" data-id="element-2164">
                      <Check className="w-2.5 h-2.5" data-id="element-2165" />
                      Claimed
                    </div>}

                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-2166">
                    <div className={`relative w-16 h-16 sm:w-20 sm:h-20 border ${affordable ? 'border-[#D4AF37]/40' : 'border-[#FDFBF7]/15'} flex items-center justify-center transition-transform duration-500 group-hover:scale-110`} data-id="element-2167">
                      <span className={`absolute -top-px left-3 right-3 h-px bg-gradient-to-r from-transparent ${affordable ? 'via-[#D4AF37]' : 'via-[#FDFBF7]/30'} to-transparent`} data-id="element-2168" />
                      <span className={`absolute -bottom-px left-3 right-3 h-px bg-gradient-to-r from-transparent ${affordable ? 'via-[#D4AF37]' : 'via-[#FDFBF7]/30'} to-transparent`} data-id="element-2169" />
                      <Icon className={`w-7 h-7 sm:w-9 sm:h-9 transition-colors ${affordable ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/35'}`} strokeWidth={1.25} data-id="element-2170" />
                    </div>
                  </div>

                  <div className="absolute bottom-3 left-3 z-10" data-id="element-2171">
                    <p className={`font-serif text-2xl leading-tight ${affordable ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/40'}`} data-id="element-2172">
                      {r.cost.toLocaleString()}
                    </p>
                    <p className="text-[#FDFBF7]/40 text-[9px] tracking-[0.25em] uppercase" data-id="element-2173">
                      XP
                    </p>
                  </div>

                  {/* Fulfillment badge — bottom right */}
                  <div className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1.5 px-2 py-1 border border-[#D4AF37]/30 bg-[#0B0F19]/80 text-[#D4AF37]/80 text-[9px] tracking-[0.2em] uppercase" data-id="element-2174">
                    <fb.icon className="w-2.5 h-2.5" data-id="element-2175" />
                    {fb.label}
                  </div>
                </div>

                {/* Body */}
                <div className="p-4 flex-1 flex flex-col" data-id="element-2176">
                  <h5 className="font-serif text-base text-[#FDFBF7] leading-tight mb-1.5" dangerouslySetInnerHTML={{
                __html: r.name
              }} data-id="element-2177" />
                  <p className="text-[#FDFBF7]/50 text-[11px] leading-relaxed mb-4 flex-1" dangerouslySetInnerHTML={{
                __html: r.desc
              }} data-id="element-2178" />
                  {isRedeemed ? <button disabled className="w-full py-2.5 text-[10px] tracking-[0.25em] uppercase font-bold bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] flex items-center justify-center gap-2 cursor-default" data-id="element-2179">
                      <Check className="w-3 h-3" data-id="element-2180" />
                      Redeemed
                    </button> : affordable ? <button onClick={() => setRedeeming(r)} className="w-full py-2.5 text-[10px] tracking-[0.25em] uppercase font-bold bg-[#D4AF37] text-[#0B0F19] hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(212,175,55,0.2)]" data-id="element-2181">
                      Redeem Now
                      <ArrowRight className="w-3 h-3" data-id="element-2182" />
                    </button> : <div className="space-y-2" data-id="element-2183">
                      <div className="h-1 bg-[#D4AF37]/10 relative overflow-hidden" data-id="element-2184">
                        <div className="absolute inset-y-0 left-0 bg-[#D4AF37]/40" style={{
                    width: `${Math.min(100, userXp / r.cost * 100)}%`
                  }} data-id="element-2185" />
                      </div>
                      <button disabled className="w-full py-2.5 text-[10px] tracking-[0.25em] uppercase font-bold bg-[#FDFBF7]/5 border border-[#FDFBF7]/15 text-[#FDFBF7]/50 cursor-not-allowed flex items-center justify-center gap-2" data-id="element-2186">
                        <Lock className="w-3 h-3" data-id="element-2187" />
                        {shortfall.toLocaleString()} XP More
                      </button>
                    </div>}
                </div>
              </motion.div>;
        })}
        </div>

        <p className="text-center text-[#FDFBF7]/35 text-[10px] tracking-[0.2em] uppercase" data-id="element-2188">
          Earn XP through daily packets, completing sets, and returning
          duplicates
        </p>
      </div>

      {/* Redemption modal */}
      {redeeming && <RedemptionModal reward={redeeming} onClose={() => setRedeeming(null)} onConfirm={handleConfirmRedeem} data-id="element-2189" />}
    </>;
};